import React, { useState, useEffect } from 'react';
import { Keyboard } from 'lucide-react';
import { Modal } from './Modal';

interface Shortcut {
  keys: string[];
  description: string;
  category: 'global' | 'navigation' | 'actions';
}

const shortcuts: Shortcut[] = [
  // Global shortcuts
  { keys: ['?'], description: 'Show keyboard shortcuts', category: 'global' },
  { keys: ['Escape'], description: 'Close modal or dialog', category: 'global' },
  { keys: ['/'], description: 'Focus search input', category: 'global' },

  // Navigation shortcuts
  { keys: ['g', 'd'], description: 'Go to Dashboard', category: 'navigation' },
  { keys: ['g', 'a'], description: 'Go to Associates', category: 'navigation' },
  { keys: ['g', 'i'], description: 'Go to Interviews', category: 'navigation' },
  { keys: ['g', 'r'], description: 'Go to Review Queue', category: 'navigation' },
  { keys: ['g', 'q'], description: 'Go to Questions', category: 'navigation' },
  { keys: ['g', 's'], description: 'Go to Settings', category: 'navigation' },

  // Action shortcuts
  { keys: ['n'], description: 'New item (context-dependent)', category: 'actions' },
  { keys: ['e'], description: 'Edit selected item', category: 'actions' },
  { keys: ['Delete'], description: 'Delete selected item', category: 'actions' },
  { keys: ['Ctrl', 's'], description: 'Save changes', category: 'actions' },
  { keys: ['Ctrl', 'k'], description: 'Open command palette', category: 'actions' },
];

const categoryLabels: Record<Shortcut['category'], string> = {
  global: 'Global',
  navigation: 'Navigation',
  actions: 'Actions',
};

export const KeyboardShortcuts: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Show shortcuts modal on '?' key
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Don't trigger if user is typing in an input
        const target = event.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        event.preventDefault();
        setIsOpen(true);
      }

      // Close modal on Escape
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const groupedShortcuts = shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category].push(shortcut);
      return acc;
    },
    {} as Record<Shortcut['category'], Shortcut[]>
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="mm-btn mm-btn-secondary fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 text-sm shadow-2xl focus:outline-none focus:ring-2 focus:ring-violet-400"
        aria-label="Show keyboard shortcuts"
        title="Keyboard Shortcuts (Press ?)"
      >
        <Keyboard size={20} aria-hidden="true" />
        <span className="hidden sm:inline">Shortcuts</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Keyboard Shortcuts"
        description="Available keyboard shortcuts for efficient navigation"
        size="lg"
      >
        <div className="space-y-6">
          <p className="text-sm text-white/70">
            Use these keyboard shortcuts to navigate and perform actions quickly.
          </p>

          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category}>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">
                {categoryLabels[category as Shortcut['category']]}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={`${category}-${index}`}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                  >
                    <span className="text-sm text-white/80">
                      {shortcut.description}
                    </span>
                    <div className="flex gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          {keyIndex > 0 && (
                            <span className="text-white/40 mx-1">then</span>
                          )}
                          <kbd className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-black/40 px-2 py-1 text-xs font-semibold text-white/80 shadow-sm min-w-[2rem]">
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Additional info */}
          <div className="rounded-2xl border border-violet-500/20 bg-violet-500/10 p-4">
            <h4 className="mb-2 text-sm font-semibold text-violet-200">
              Navigation Tips
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-violet-100/80">
              <li>Use Tab to move forward through interactive elements</li>
              <li>Use Shift + Tab to move backward</li>
              <li>Use Arrow keys to navigate within lists and menus</li>
              <li>Use Enter or Space to activate buttons and links</li>
              <li>Press Escape to close modals and dialogs</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default KeyboardShortcuts;

// Global keyboard shortcut handler hook
export const useGlobalShortcuts = (navigate?: (path: string) => void) => {
  useEffect(() => {
    if (!navigate) return;

    let lastKey = '';
    let lastKeyTime = 0;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Don't trigger if user is typing in an input
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const now = Date.now();
      const key = event.key.toLowerCase();

      // Handle 'g' prefix for navigation shortcuts
      if (lastKey === 'g' && now - lastKeyTime < 1000) {
        event.preventDefault();

        switch (key) {
          case 'd':
            navigate('/dashboard');
            break;
          case 'a':
            navigate('/associates');
            break;
          case 'i':
            navigate('/interviews');
            break;
          case 'r':
            navigate('/review-queue');
            break;
          case 'q':
            navigate('/questions');
            break;
          case 's':
            navigate('/settings');
            break;
        }

        lastKey = '';
        lastKeyTime = 0;
      } else if (key === 'g') {
        lastKey = 'g';
        lastKeyTime = now;
      } else {
        lastKey = '';
        lastKeyTime = 0;
      }

      // Handle search shortcut
      if (key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        const searchInput = document.getElementById('search');
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Handle command palette
      if (key === 'k' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        // Trigger command palette (to be implemented)
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate]);
};
